import React, { useState } from "react";
import axios from "axios";
import { Send, Loader2, CheckCircle2, TrendingUp } from "lucide-react";
import { useToast } from "../hooks/use-toast";
import { BRAND } from "../mock";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INTERESTS = [
  "Land acquisition",
  "Build-to-Rent",
  "Joint venture / partnership",
  "General investment",
];
const LEVELS = ["Under £250k", "£250k – £1m", "£1m – £5m", "£5m+"];

export default function InvestorForm() {
  const { toast } = useToast();
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    interest: "",
    level: "",
    message: "",
  });

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const reset = () =>
    setForm({ firstName: "", lastName: "", email: "", phone: "", interest: "", level: "", message: "" });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    const payload = {
      firstName: form.firstName,
      lastName: form.lastName,
      email: form.email,
      phone: form.phone,
      service: `Investor Enquiry${form.interest ? " — " + form.interest : ""}`,
      message:
        `Investment interest: ${form.interest || "-"}\n` +
        `Investment level: ${form.level || "-"}\n\n` +
        `${form.message}`,
    };
    try {
      await axios.post(`${API}/contact`, payload);
      setSubmitted(true);
      toast({
        title: "Enquiry sent",
        description: `Thanks ${form.firstName || ""}! Our investment team will be in touch shortly.`,
      });
    } catch (err) {
      const saved = err?.response?.status === 502;
      toast({
        title: saved ? "Enquiry received" : "Something went wrong",
        description: saved
          ? "We've logged your enquiry but email delivery is delayed. We'll still get back to you."
          : `Please try again, or call us on ${BRAND.phone}.`,
        variant: saved ? undefined : "destructive",
      });
      if (saved) setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls =
    "w-full rounded-md border border-slate-200 px-4 py-3 text-sm outline-none transition focus:border-[#1F3463] focus:ring-2 focus:ring-[#1F3463]/20";

  return (
    <div className="rounded-2xl bg-white p-8 shadow-2xl">
      {submitted ? (
        <div className="flex flex-col items-center py-12 text-center">
          <CheckCircle2 className="h-16 w-16 text-[#C9A876]" />
          <h3 className="mt-5 font-display text-2xl font-bold text-[#1F3463]">Thank you</h3>
          <p className="mt-3 max-w-md text-slate-600">
            Your investor enquiry has been received. Our investment team will be in
            touch very soon.
          </p>
          <button
            onClick={() => {
              setSubmitted(false);
              reset();
            }}
            className="btn-outline-navy mt-7 rounded-md px-6 py-3 text-sm font-semibold uppercase tracking-wide"
          >
            Submit another enquiry
          </button>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="flex items-center gap-3">
            <span className="inline-flex h-11 w-11 items-center justify-center rounded-lg bg-[#F5F1E8] text-[#1F3463]">
              <TrendingUp className="h-5 w-5" />
            </span>
            <h3 className="font-display text-xl font-bold text-[#1F3463]">Register your interest</h3>
          </div>

          <div className="grid gap-5 sm:grid-cols-2">
            <div>
              <label className="mb-1.5 block text-sm font-medium text-[#1F3463]">
                First name<span className="text-[#C9A876]"> *</span>
              </label>
              <input className={inputCls} value={form.firstName} onChange={update("firstName")} required />
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-medium text-[#1F3463]">Last name</label>
              <input className={inputCls} value={form.lastName} onChange={update("lastName")} />
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-medium text-[#1F3463]">
                Email<span className="text-[#C9A876]"> *</span>
              </label>
              <input type="email" className={inputCls} value={form.email} onChange={update("email")} required />
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-medium text-[#1F3463]">Phone number</label>
              <input type="tel" className={inputCls} value={form.phone} onChange={update("phone")} />
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-medium text-[#1F3463]">Area of interest</label>
              <select className={inputCls} value={form.interest} onChange={update("interest")}>
                <option value="">Select…</option>
                {INTERESTS.map((o) => (
                  <option key={o} value={o}>
                    {o}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-medium text-[#1F3463]">Investment level</label>
              <select className={inputCls} value={form.level} onChange={update("level")}>
                <option value="">Select…</option>
                {LEVELS.map((o) => (
                  <option key={o} value={o}>
                    {o}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="mb-1.5 block text-sm font-medium text-[#1F3463]">
              Message<span className="text-[#C9A876]"> *</span>
            </label>
            <textarea
              rows={4}
              required
              className={`${inputCls} resize-none`}
              value={form.message}
              onChange={update("message")}
              placeholder="Tell us about the opportunity or what you're looking for…"
            />
          </div>

          <button
            type="submit"
            disabled={submitting}
            className="btn-navy flex w-full items-center justify-center gap-2 rounded-md px-6 py-4 text-sm font-semibold uppercase tracking-wide disabled:cursor-not-allowed disabled:opacity-70 sm:w-auto"
          >
            {submitting ? (
              <>
                Sending <Loader2 className="h-4 w-4 animate-spin" />
              </>
            ) : (
              <>
                Submit enquiry <Send className="h-4 w-4" />
              </>
            )}
          </button>
        </form>
      )}
    </div>
  );
}
